import { ContentType } from '@/@types/common'
import { PostStatus, type Post } from '@/@types/post'
import ShareIcon from '@/assets/share.svg?react'
import ActionButton, { type ActionButtonProps } from '@/components/ActionButton'
import { BlurTextMode, FeaturableBlurTextSwitch } from '@/components/BlurTextSwitch'
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu'
import type { MenuItemData } from '@/components/DropdownMenu/types'
import createShareImageMenuItem from '@/components/ShareImage/createShareImageMenuItem'
import { POST_TYPE_TO_ROUTE_MAPPING } from '@/utils/route'
import { compact } from 'lodash-es'
import styles from './RepostMenuButton.module.css'
import { type HTMLAttributes, useCallback, useMemo } from 'react'
import toast from 'react-hot-toast'
import { ACTION_MENU_LOCALE, ActionMenu } from '../config'
import { showRepostFormModal } from '../utils'

export interface RepostMenuButtonProps extends HTMLAttributes<HTMLDivElement> {
	data: Post
}

export default function RepostMenuButton({ data, ...props }: RepostMenuButtonProps) {
	const { shareCount } = data
	// 推荐流的精简动态不返回 repostable，不应依赖分享图加载详情后才显示转发入口。
	const repostable =
		data.repostable ??
		((data.type === ContentType.POST || data.type === ContentType.REPOST) &&
			!data.isPrivate &&
			!data.deleted &&
			data.status !== PostStatus.DELETED)
	const menuData = useMemo(
		() =>
			compact([
				repostable && ACTION_MENU_LOCALE[ActionMenu.REPOST],
				ACTION_MENU_LOCALE[ActionMenu.COPY_LINK],
				createShareImageMenuItem({ kind: 'post', data }),
			]) as MenuItemData[],
		[data, repostable]
	)

	const handleSelect = useCallback(
		(menuId: string) => {
			switch (menuId) {
				case ActionMenu.COPY_LINK:
					{
						const username = data?.user?.username
						if (!username) return
						const postType = POST_TYPE_TO_ROUTE_MAPPING[data.type]
						const url = new URL(`/u/${username}/${postType}/${data.id}`, window.location.origin)
						navigator.clipboard
							.writeText(url.toString())
							.then(() => {
								toast.success('已复制链接')
							})
							.catch(() => {
								toast.error('复制失败')
							})
					}
					break
				case ActionMenu.REPOST:
					showRepostFormModal(data)
					break
			}
		},
		[data]
	)

	return (
		<DropdownMenu
			data={menuData}
			onSelect={handleSelect}
			position="top-start"
			renderTrigger={(props) => (
				<ActionButton
					tooltip="分享"
					icon={<ShareIcon width="20px" height="20px" className={styles.icon} />}
					{...(props as ActionButtonProps)}
				>
					<FeaturableBlurTextSwitch data={(shareCount ?? 0).toString()} mode={BlurTextMode.SMALL} />
				</ActionButton>
			)}
		/>
	)
}
